'use client'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslations } from 'next-intl'
import GridWhite from '../../assets/images/estateSearch/grid-white.svg'
import GridGray from '../../assets/images/estateSearch/grid-gray.svg'
import ListWhite from '../../assets/images/estateSearch/list-white.svg'
import ListGray from '../../assets/images/estateSearch/list-gray.svg'
import { Sheet } from 'react-modal-sheet'
import { categoryTranslations } from '../../helpers/utils'
import { isMobile } from 'react-device-detect'
import Select from 'react-select'
import Image from 'next/image'
import { Spinner } from 'reactstrap'
import Card from '../../components/Card'
import Pagination from '../components/Pagination'
import { useSearchParams } from 'next/navigation'
import SearchParametersComponent from '../components/SearchParameters'
import { APIClient } from '../../helpers/api_helper'
import ListCard from '../../components/ListCard'

const ClientSearchPage = ({
  category,
  type,
  rooms,
  building_type,
  searchParamsData = [],
  initialInformation = [],
  locale
}: {
  category: any
  type: any
  rooms: any
  building_type: any
  searchParamsData: any
  initialInformation: any
  locale: string
}) => {
  const t = useTranslations('')
  const queryParameters = useSearchParams();
  const api = new APIClient();

  const stableInitialInformation = useMemo(
    () => initialInformation,
    [initialInformation]
  )
  const stableSearchParamsData = useMemo(
    () => searchParamsData,
    [searchParamsData]
  )

  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isOpen, setOpen] = useState(false);
  const [isSortByOpen, setSortByOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isUrlDecoded, setIsUrlDecoded] = useState(false);
  const [data, setData] = useState([]);
  const defaultGridView = 'grid-view';
  const [viewType, setViewType] = useState(defaultGridView);
  const [sorting, setSorting] = useState('created_at');
  const searchFilters = [
    {value: 'created_at', label: t('Sort By Date')},
    {value: 'price-low', label: t('from low to high')},
    {value: 'price-high', label: t('from high to low')}
  ];

  const totalPages = Math.ceil(totalCount / 20);
  const categoryNameUrlAlias: any = {
    "apartment": "apartments",
    "commercial-property": "commercial-properties",
    "land": "land",
    "house": "houses",
  };
  const [formData, setFormData]: any = useState({
    category: category,
    type: type,
    regions: [],
    code: '',
    parameters: {},
    parametersFlat: {},
    multiSelectParameters: {},
  });

  const setUrlParams = (queryParameters: any) => {
    const tmp: any = {
      parameters: {},
      parametersFlat: {}
    };
    const multipleSelects: string[] = ['8', '37', '11']
    const multipleSelectsValues: any = {}

    tmp.code = queryParameters.get('code') || ''
    tmp.regions = queryParameters.get('regions') || []

    if (tmp.regions.length > 0) {
      tmp.regions = tmp.regions.replace(/["[\]\s]/g, '')
      tmp.regions = tmp.regions.split(",")
    }
    multipleSelects.forEach((item) => {
      const param = queryParameters.get(item)
      if (param) {
        multipleSelectsValues[item] = param.split(',')
      }
    })

    Object.entries(multipleSelectsValues).forEach((item) => {
      tmp.parameters[item[0]] = item[1]
    })

    searchParamsData.forEach((item: any) => {
      if (item.type === 'Number' || item.type === 'Decimal number' || item.type === 'Comma formatted') {
        const min = queryParameters.get(item.id + '-min');
        const max = queryParameters.get(item.id + '-max');

        if (min || max) {
          tmp.parameters[item.id] = {}
          if (min) {
            tmp.parameters[item.id].min = min
            tmp.parametersFlat[`${item.id}-min`] = min
          }
          if (max) {
            tmp.parameters[item.id].max = max
            tmp.parametersFlat[`${item.id}-max`] = min
          }
        }
      }

      if (item.id === 11) {
        if (rooms) {
          tmp.parameters[item.id] = [rooms.toString()]
        }
      }
      if (item.id === 8) {
        if (building_type) {
          tmp.parameters[item.id] = [building_type.toString()]
        }
      }
    });
    return tmp;
  }

  useEffect(() => {
    if (searchParamsData.length) {
      const urlParams = setUrlParams(queryParameters);

      setFormData((prevFormData: any) => {
        if (JSON.stringify(prevFormData.parameters) === JSON.stringify(urlParams.parameters)) {
          return prevFormData;
        }

        return {
          ...prevFormData,
          parameters: urlParams.parameters,
          parametersFlat: urlParams.parametersFlat,
          code: urlParams.code,
          regions: urlParams.regions
        };
      });
      setIsUrlDecoded(true)
    }

  }, [queryParameters]);

  useEffect(() => {
    const fetchDataAndDecodeURL = async () => {
      if (searchParamsData.length && !isUrlDecoded) {
        const urlParams = setUrlParams(queryParameters);

        setFormData((prevFormData: any) => ({
          ...prevFormData,
          parameters: urlParams.parameters,
          parametersFlat: urlParams.parametersFlat,
          code: urlParams.code,
          regions: urlParams.regions
        }));

        setIsUrlDecoded(true);
      }

      if (isUrlDecoded && initialInformation.categories.length) {
        try {
          const headers = { 'Accept-Language': locale };
          const requestData = {
            category: formData.category,
            type: formData.type,
            code: formData.code,
            page: currentPage,
            parameters: JSON.stringify(formData.parameters),
            regions: formData.regions.join(','),
            sort_by: sorting
          };

          const response: any = await api.get('/site/estate/', requestData, headers);

          setTotalCount(response.count || 0);
          setData(response.results);
          setLoading(false);
        } catch (error) {
          console.error('Error fetching estate data', error);
          setLoading(false);
        }
      }
    };

    fetchDataAndDecodeURL();
  }, [searchParamsData.length, isUrlDecoded, initialInformation, currentPage, sorting]);

  const handleSortingChange = (e: any) => {
    setSorting(e.value)
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= Math.ceil(totalCount / 20)) {
      setCurrentPage(page);

      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleMobileSortingChange = (e: any) => {
    setSorting(e.target.value)
    setSortByOpen(false)
  };


  const handleSearchSubmit = (e: any) => {
    e.preventDefault();

    let url = `/${locale}/`;
    const baseUrl = categoryNameUrlAlias[formData.category.replace(/_/g, "-")]

    if (formData.type === "Sale") {
      url += baseUrl+"-for-sale?";
    } else {
      url += baseUrl+"-for-rent?";
    }

    if (formData.code) {
      url += `code=${formData.code}`;
    }

    if (formData.regions) {
      url += `${formData.code ? '&' : ''}regions=[${formData.regions.join(',')}]`;
    }

    url += '&' + Object.keys(formData.parametersFlat)
      .map(key => {
        if (!formData.parametersFlat[key]) {
          return false;
        }

        if (key === "11") {
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(formData.parametersFlat[key])}`;
      });

    window.location.href = url

    // fetchData();

    setOpen(false)
    setSortByOpen(false)
  };

  return (
    <>
      <main>
        <section className='filter-section'>
          <div className='container-main'>
            <div className='top-section'>
              <div className='search-top-left'>
                <div className='heading-block'>
                  <h1>{t(`For ${type}`)}</h1>
                </div>
                <div className='category-and-result-block'>
                  <p>{t(categoryTranslations[category])}</p>
                  <small>
                    {totalCount} {t('results have been found')}
                  </small>
                </div>


                <div className='mobile mb-24'>
                  <button
                    className='mobile-open-search-filters-button'
                    onClick={() => {
                      setOpen(true)
                      setSortByOpen(false)
                    }}
                  >
                    {t('Search by Filters')}
                  </button>
                </div>
              </div>

              <div className='search-top-right'>
                <div className='view-type-container'>
                  <div
                    className={`grid-type-button ${viewType === 'grid-view' ? 'gtb-active' : ''}`}
                    title='Grid'
                    onClick={() => setViewType('grid-view')}
                    style={{ cursor: 'pointer', display: 'inline-block' }}
                  >
                    <Image
                      src={viewType === 'grid-view' ? GridWhite : GridGray}
                      width="18"
                      height="18"
                      alt='grid-view'
                    />
                  </div>
                  <div
                    className={`grid-type-button ${viewType === 'row-view' ? 'gtb-active' : ''}`}
                    title='Row'
                    onClick={() => setViewType('row-view')}
                    style={{ cursor: 'pointer', display: 'inline-block' }}
                  >
                    <Image
                      src={viewType === 'row-view' ? ListWhite : ListGray}
                      width="18"
                      height="18"
                      alt='list-view'
                    />
                  </div>
                </div>
                <Select
                  className='react-select-container sort-by no-mobile'
                  options={searchFilters}
                  value={searchFilters.filter(option => {
                    return sorting === option.value
                  })}
                  onChange={handleSortingChange}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      padding: '0px',
                      fontSize: '13px'
                    }),
                    menu: baseStyles => ({
                      ...baseStyles,
                      zIndex: 11
                    })
                  }}
                />

                <button
                  className='mobile sort-by-button-mobile'
                  onClick={() => {
                    setSortByOpen(true)
                    setOpen(false)
                  }}
                >
                  {t('Sort By')}
                </button>
              </div>
            </div>
            <div className='search-block'>
              <div className='no-mobile'>
                <SearchParametersComponent
                  selectedLang={locale}
                  formData={formData}
                  setFormData={setFormData}
                  handleSearchSubmit={handleSearchSubmit}
                  showTitle={true}
                  initialData={initialInformation}
                  searchPageParameters={searchParamsData}
                />
              </div>

              <>
                <div className='top-section no-mobile w-100 justify-content-start d-lg-none mb-0'>
                  <div className='search-block-right-top w-100 no-mobile'>
                    <div className='w-100'>
                      <span
                        id='founded-result'
                        className='found-text'
                        style={{ marginLeft: '10px' }}
                      >
                        {totalCount} {t('results have been found')}
                      </span>
                    </div>
                  </div>
                  <div className='form-group no-mobile'>
                    <select className='sort-by-input' id='sortByMobile'>
                      <option value='created_asc'>{t('Sort By Date')}</option>
                      <option value='high'>{t('from low to high')}</option>
                      <option value='low'>{t('from high to low')}</option>
                    </select>
                  </div>
                </div>
                <div className='search-block-right'>
                  {loading ? (
                    <Spinner className='search-spinner me-2 text-center'>
                      {' '}
                      {t('Loading') + '...'}{' '}
                    </Spinner>
                  ) : (
                    <>
                      {viewType === 'row-view' ? (
                        <div
                          id='estate-list-view'
                          className='search-block-right-content-row'
                        >
                          {data.map((item: any) => (
                            <ListCard
                              key={item.id}
                              data={item}
                              category={item.category}
                            />
                          ))}
                        </div>
                      ) : (
                        <div
                          id='estate-list-view'
                          className='search-block-right-content'
                        >
                          {data.map((item: any) => (
                            <Card
                              key={item.id}
                              data={item}
                              withSlider={true}
                            />
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            </div>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </section>
      </main>

      <Sheet
        detent={'content-height'}
        isOpen={isOpen}
        disableScrollLocking={true}
        onClose={() => setOpen(false)}
      >
        <Sheet.Container>
          <Sheet.Content>
            <h6
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '16px'
              }}
            >
              <div>
                <p className='mobile_form_header_text'>
                  {t('Search by Filters')}
                </p>
              </div>
              <i className='icon-close' onClick={() => setOpen(false)}></i>
            </h6>

            <Sheet.Scroller style={{'overflowX': 'hidden'}}>
              <SearchParametersComponent
                selectedLang={locale}
                formData={formData}
                setFormData={setFormData}
                handleSearchSubmit={handleSearchSubmit}
                showTitle={false}
                initialData={initialInformation}
                searchPageParameters={searchParamsData}
              />
            </Sheet.Scroller>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop />
      </Sheet>

      <Sheet
        detent={'content-height'}
        isOpen={isSortByOpen}
        onClose={() => setSortByOpen(false)}
      >
        <Sheet.Container>
          <Sheet.Content>
            <div className='sort-by-mobile-modal' style={{ height: 250 }}>
              <h6>
                <span>{t('Sort By')}</span>
                <i
                  className='icon-close'
                  onClick={() => setSortByOpen(!isSortByOpen)}
                ></i>
              </h6>

              <div className='form-check mb-2'>
                <input
                  className='form-check-input'
                  type='radio'
                  name='flexRadioDefault'
                  id='date'
                  value='created_at'
                  checked={sorting === 'created_at'}
                  onChange={handleMobileSortingChange}
                />
                <label className='form-check-label' htmlFor='date'>
                  {t('Sort By Date')}
                </label>
              </div>
              <div className='form-check mb-2'>
                <input
                  className='form-check-input'
                  type='radio'
                  name='flexRadioDefault'
                  id='lowToHigh'
                  value='price-low'
                  checked={sorting === 'price-low'}
                  onChange={handleMobileSortingChange}
                />
                <label className='form-check-label' htmlFor='lowToHigh'>
                  {t('from low to high')}
                </label>
              </div>
              <div className='form-check mb-16 mb-2'>
                <input
                  className='form-check-input'
                  type='radio'
                  name='flexRadioDefault'
                  id='higeToLow'
                  value='price-high'
                  checked={sorting === 'price-high'}
                  onChange={handleMobileSortingChange}
                />
                <label className='form-check-label' htmlFor='higeToLow'>
                  {t('from high to low')}
                </label>
              </div>
            </div>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop />
      </Sheet>
    </>
  )
}

export default ClientSearchPage
